import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createApiClient } from '../../api/client';
import BarChart from '../../components/BarChart';
import { getLastNDays, countByDate, countUniqueMobileByDate, uniqueMobiles } from '../../utils/adminStats';
import '../mobile.css';

const STATS_WINDOW_DAYS = 7;

const downloadMobileList = (mobiles, filename) => {
  const content = mobiles.join('\n');
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

const MobileAdminPage = ({ API_URL }) => {
  const navigate = useNavigate();
  const role = localStorage.getItem('role');

  const [newUsersList, setNewUsersList] = useState([]);
  const [activeUsersList, setActiveUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (role !== 'admin') {
      navigate('/m/cases', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const fetchStats = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const days = getLastNDays(STATS_WINDOW_DAYS);
      const startStr = days[0].key;
      const endStr = days[days.length - 1].key;
      const api = createApiClient(API_URL);

      const [newUsersRes, activeUsersRes] = await Promise.all([
        api.get(`/user/list/${startStr}/${endStr}`),
        api.get(`/user/log/${startStr}/${endStr}`),
      ]);

      setNewUsersList(newUsersRes.data || []);
      setActiveUsersList(activeUsersRes.data || []);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setErrorMessage('Could not load admin stats. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role === 'admin') fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  if (role !== 'admin') return null;

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
        <div className="m-spinner" style={{ width: '32px', height: '32px', borderColor: '#eee', borderTopColor: '#094268' }} />
        <p>Loading admin stats...</p>
      </div>
    );
  }

  const newUsersChart = countByDate(newUsersList, STATS_WINDOW_DAYS);
  const activeUsersChart = countUniqueMobileByDate(activeUsersList, STATS_WINDOW_DAYS);
  const activeUsersTotal = uniqueMobiles(activeUsersList).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#094268', margin: 0 }}>Admin Dashboard</h2>
        <button onClick={fetchStats} className="m-pill-btn" style={{ color: '#007AFF', fontWeight: 600 }}>Refresh</button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700 }}>
          New Users <span style={{ color: '#094268' }}>({newUsersList.length})</span>
        </div>
        <BarChart data={newUsersChart} color="#094268" height={140} />
        <button
          className="m-pill-btn"
          style={{ color: '#094268', fontWeight: 600, marginTop: '6px' }}
          onClick={() => downloadMobileList(uniqueMobiles(newUsersList), 'new_users.txt')}
          disabled={newUsersList.length === 0}
        >
          Download New Users
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700 }}>
          Active Users <span style={{ color: '#094268' }}>({activeUsersTotal})</span>
        </div>
        <BarChart data={activeUsersChart} color="#f37021" height={140} />
        <button
          className="m-pill-btn"
          style={{ color: '#094268', fontWeight: 600, marginTop: '6px' }}
          onClick={() => downloadMobileList(uniqueMobiles(activeUsersList), 'active_users.txt')}
          disabled={activeUsersList.length === 0}
        >
          Download Active Users
        </button>
      </div>

      {errorMessage && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className="m-button-primary" onClick={fetchStats}>Retry</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAdminPage;
